import { Auth, signInWithCustomToken } from 'firebase/auth'
import type { IOAuthProvider } from '@/networks/api/v2/user-api.service'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'

export type ChildWindowEventData =
  | {
      type: 'customToken'
      customToken: string
    }
  | {
      /**
       * provider 가 이메일을 주지 않았거나, provider 가 인증하지 않은 이메일이 기존 계정 이메일과 같아
       * 백엔드가 이메일 인증을 요구했다 (result=no_email)
       */
      type: 'noEmail'
      provider: string
      accessToken?: string
      idToken?: string
    }

/**
 * OAuth 로그인 중 백엔드가 이메일 인증을 요구했을 때 던지는 오류.
 * 호출부는 provider 토큰으로 이메일 인증(POST /oauth/verify-email) 화면을 띄운다.
 */
export class OAuthEmailRequiredError extends Error {
  constructor(
    public readonly provider: string,
    public readonly accessToken?: string,
    public readonly idToken?: string,
  ) {
    super('Email verification required')
    this.name = 'OAuthEmailRequiredError'
  }
}

export class OAuthProcessor {
  protected customParameters: Record<string, unknown> = {}

  constructor(
    protected auth: Auth,
    protected providerString: string,
    private providerInfo: IOAuthProvider,
  ) {}

  public setCustomParameters(customParameters: Record<string, unknown>) {
    this.customParameters = customParameters
  }

  private getAuthorizationUrl() {
    const url = new URL(this.providerInfo.authorizationEndpoint)
    url.searchParams.append('client_id', this.providerInfo.clientId)
    url.searchParams.append('redirect_uri', this.getRedirectUrl())
    url.searchParams.append('response_type', 'code')
    url.searchParams.append('prompt', 'select_account')
    if (!url.searchParams.get('scope'))
      url.searchParams.append('scope', 'email')
    url.searchParams.append('response_mode', 'form_post')
    /**
     * 백엔드에서 state 의 version 을 보고 redirect_uri 를 결정한다.
     */
    url.searchParams.append('state', this.getState())

    for (const [key, value] of Object.entries(this.customParameters)) {
      url.searchParams.append(key, String(value))
    }

    /**
     * apple 의 faceid 같은 appleID 는 authorization url 로 바로 이동시
     * 창이 open 되지 않아서 process 가 진행되지 않기 때문에
     * beforeOAuthUrl 에서 next 로 redirect
     */
    const befoerOAuthUrl = new URL(
      window.location.protocol + window.location.host,
    )
    befoerOAuthUrl.pathname = `/signin/oauth/before-oauth`
    befoerOAuthUrl.searchParams.append(
      'next',
      encodeURIComponent(url.toString()),
    )
    return befoerOAuthUrl.toString()
  }

  protected getState() {
    return encodeURIComponent(
      JSON.stringify({
        version: 2,
      }),
    )
  }

  protected getRedirectUrl() {
    return this.providerInfo.redirectUrl.walletV2
  }

  private checkClosedPopupWindow(childWindow: Window) {
    return new Promise<void>((_, reject) => {
      const timer = setInterval(function () {
        if (childWindow.closed) {
          clearInterval(timer)
          reject(new Error('User closed popup window'))
        }
      }, 1000)
    })
  }

  /**
   * popupLogin 수행 후 idtoken을 반환한다.
   */
  public async doPopupLogin() {
    const childWindow = window.open(
      this.getAuthorizationUrl(),
      '_blank',
      'popup=true,width=360,height=640',
    )
    if (!childWindow) {
      throw new Error('Cannot open popup window')
    }
    return new Promise<string>(async (resolve, reject) => {
      try {
        this.checkClosedPopupWindow(childWindow).catch(reject)
        const result = await this.getResultFromChildWindow()
        if (result.type === 'noEmail') {
          throw new OAuthEmailRequiredError(
            result.provider,
            result.accessToken,
            result.idToken,
          )
        }

        const userCredential = await signInWithCustomToken(
          this.auth,
          result.customToken,
        )
        const idToken = await userCredential.user.getIdToken()
        resolve(idToken)
      } catch (e) {
        reject(e)
      }
    })
  }
  /**
   * 팝업에서 로그인 결과(customToken 또는 이메일 인증 요구)를 받는다.
   * COOP(Cross-Origin-Opener-Policy) 정책으로 인해 window.opener가 null일 수 있어서
   * postMessage와 BroadcastChannel 두 가지 방식을 모두 리스닝한다.
   */
  private getResultFromChildWindow() {
    return new Promise<ChildWindowEventData>((resolve) => {
      let resolved = false

      const handleToken = (data: ChildWindowEventData) => {
        if (
          !resolved &&
          (data?.type === 'customToken' || data?.type === 'noEmail')
        ) {
          resolved = true
          cleanup()
          resolve(data)
        }
      }

      // 1. postMessage 리스너 (기존 방식)
      const messageHandler = (event: MessageEvent) => {
        handleToken(event.data as ChildWindowEventData)
      }
      window.addEventListener('message', messageHandler)

      // 2. BroadcastChannel 리스너 (COOP fallback)
      const channel = new BroadcastChannel('wepin_oauth_channel')
      channel.onmessage = (event: MessageEvent) => {
        handleToken(event.data as ChildWindowEventData)
      }

      const cleanup = () => {
        window.removeEventListener('message', messageHandler)
        channel.close()
      }
    })
  }
}

export async function getOAuthProcessor(auth: Auth, providerString: string) {
  const api = new WepinBackendAPI()
  const providerInfo = await api.user.getOauthProviderInfoList()
  const provider = providerInfo.find(
    (provider: any) =>
      provider.provider.toLowerCase() === providerString.toLowerCase(),
  )
  if (provider) {
    return new OAuthProcessor(auth, provider.provider, provider)
  }
}
